import { useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { normalizeJoinCode } from "@vocifuchs/shared";
import { api, ApiError } from "../lib/api.js";
import { auth } from "../copy/auth.js";
import { common } from "../copy/common.js";
import { errors } from "../copy/errors.js";
import { Banner, Button } from "../components/ui.js";
import { ClassCodeField } from "../components/ClassCodeField.js";
import { useClassPreview, useCodeFromLink } from "../lib/join.js";
import { useSession } from "../session.js";

export function JoinScreen() {
  const navigate = useNavigate();
  const { membership, refresh } = useSession();
  // Filled from a class link (issue #48), also when a logged-in child opens it.
  const [joinCode, setJoinCode] = useCodeFromLink();
  const preview = useClassPreview(normalizeJoinCode(joinCode));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (membership) return <Navigate to="/" replace />;
  const ready = preview.state === "found";

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    const code = normalizeJoinCode(joinCode);
    if (code.length !== 8) {
      setError(errors.joinCodeLength);
      return;
    }
    if (!ready) {
      if (preview.state === "unknown") setError(preview.message);
      return;
    }
    setBusy(true);
    try {
      await api("/api/classes/join", {
        method: "POST",
        body: JSON.stringify({ join_code: code }),
      });
      await refresh();
      navigate("/", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : errors.failed);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-md space-y-6">
      <Link to="/me" className="inline-flex font-semibold text-fox-ink underline-offset-4 hover:underline">
        {common.backToMe}
      </Link>
      <h1 className="text-3xl sm:text-4xl">{auth.join.title}</h1>
      <p className="text-ink-soft">{auth.join.intro}</p>
      <form className="space-y-4" onSubmit={(e) => void submit(e)}>
        {error ? <Banner>{error}</Banner> : null}
        <ClassCodeField
          label={auth.join.classCode}
          value={joinCode}
          onChange={setJoinCode}
          preview={preview}
        />
        <Button type="submit" disabled={busy || !ready}>
          {busy ? common.loading : auth.join.submit}
        </Button>
      </form>
    </div>
  );
}
